import { mapStyles } from '../components';
import L from 'leaflet';
import { useCallback, useEffect, useRef, useState } from 'react';

const MAP_STYLE_STORAGE_KEY = 'property-map-style';

interface UsePropertyMapProps {
    latitude: number;
    longitude: number;
    onLocationChange?: (lat: number, lng: number) => void;
}

export const usePropertyMap = ({ latitude, longitude, onLocationChange }: UsePropertyMapProps) => {
    const [activeMapStyle, setActiveMapStyle] = useState<keyof typeof mapStyles>(() => {
        const savedStyle = localStorage.getItem(MAP_STYLE_STORAGE_KEY);
        return (savedStyle && savedStyle in mapStyles)
            ? savedStyle as keyof typeof mapStyles
            : "dark";
    });

    const mapRef = useRef<L.Map | null>(null);
    const radiusSize = 100;

    useEffect(() => {
        localStorage.setItem(MAP_STYLE_STORAGE_KEY, activeMapStyle);
    }, [activeMapStyle]);

    const handleMarkerDragEnd = useCallback((lat: number, lng: number) => {
        if (onLocationChange) {
            onLocationChange(lat, lng);
        }
    }, [onLocationChange]);

    const handleCenterMap = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        if (mapRef.current) {
            mapRef.current.setView([latitude, longitude], 14, {
                animate: true,
                duration: 1
            });
        }
    }, [latitude, longitude]);

    const setMapRef = useCallback((map: L.Map) => {
        mapRef.current = map;
    }, []);

    const handleMapStyleChange = useCallback((value: string) => {
        setActiveMapStyle(value as keyof typeof mapStyles);
    }, []);

    useEffect(() => {
        const styleElement = document.createElement('style');
        styleElement.innerHTML = `
            .leaflet-custom-popup .leaflet-popup-content-wrapper {
                border-radius: 8px;
                box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
            }
            
            .leaflet-custom-popup .leaflet-popup-content {
                margin: 8px 12px;
            }
            
            .leaflet-custom-popup .leaflet-popup-tip {
                box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
            }
            
            .custom-marker {
                transition: transform 0.2s ease-out;
            }
            
            .leaflet-marker-draggable .custom-marker:hover {
                transform: scale(1.1);
            }
            
            .marker-dragging {
                z-index: 1000 !important;
            }
            
            .marker-dragging .custom-marker {
                transform: scale(1.2);
                filter: drop-shadow(0 0 5px rgba(0, 0, 0, 0.3));
            }
            
            .leaflet-control-attribution {
                font-size: 8px !important;
                background-color: rgba(255, 255, 255, 0.5) !important;
                padding: 2px 4px !important;
                border-radius: 3px !important;
                color: #888 !important;
            }
            
            @media (max-width: 768px) {
                .leaflet-control-attribution {
                    display: none !important;
                }
            }
            
            .leaflet-container {
                z-index: 0 !important;
            }
            
            .leaflet-control-container {
                z-index: 1000 !important;
            }
            
            .map-control-button {
                background-color: white !important;
                box-shadow: 0 2px 5px rgba(0,0,0,0.2) !important;
                border: none !important;
                border-radius: 50% !important;
                width: 32px !important;
                height: 32px !important;
                display: flex !important;
                align-items: center !important;
                justify-content: center !important;
                cursor: pointer !important;
                transition: all 0.2s ease !important;
            }
            
            .map-control-button:hover {
                transform: scale(1.1) !important;
                box-shadow: 0 3px 8px rgba(0,0,0,0.3) !important;
            }
        `;

        document.head.appendChild(styleElement);

        return () => {
            document.head.removeChild(styleElement);
        };
    }, []);

    return {
        activeMapStyle,
        mapRef,
        radiusSize,
        handleMarkerDragEnd,
        handleCenterMap,
        setMapRef,
        handleMapStyleChange
    };
}; 